import { useState, useEffect, useRef, useCallback } from 'react';

import { getAsset } from '../../../util/assets';
import { WALLPAPER_WATERMARK_MODE } from '../../../constants/pro-settings';

// Cache for storing processed watermark images
const imageCache = new Map();

const createCanvas = (width, height) => {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  return canvas;
};

const setupCanvasContext = canvas => {
  const ctx = canvas.getContext('2d');
  ctx.save();
  ctx.translate(canvas.width / 2, canvas.height / 2);
  ctx.rotate((-45 * Math.PI) / 180);
  return ctx;
};

const drawPattern = (context, drawFn, stepX, stepY, canvas) => {
  let rowOffset = 0;
  const rows = Math.ceil((canvas.height * 2) / stepY);
  const cols = Math.ceil((canvas.width * 2) / stepX);

  for (let row = -rows; row <= rows; row += 1) {
    // Alternate the offset for each row to create staggered pattern
    rowOffset = row % 2 === 0 ? 0 : stepX / 2;

    for (let col = -cols; col <= cols; col += 1) {
      const x = col * stepX + rowOffset;
      const y = row * stepY;
      drawFn(context, x, y);
    }
  }
  context.restore();
};

// Generate a cache key based on image parameters
const getCacheKey = (url, width, height, mode) =>
  `${url}:${width}:${height}:${mode}`;

const processImage = async (
  img,
  offsetWidth,
  offsetHeight,
  wallpaperMode,
  cacheKey,
) => {
  try {
    const imgElem = img;
    const maxSize = Math.min(offsetWidth, offsetHeight) * 0.85;
    const scale = Math.min(maxSize / imgElem.width, maxSize / imgElem.height);
    const scaledWidth = imgElem.width * scale;
    const scaledHeight = imgElem.height * scale;

    const stepX = scaledWidth * 2.5;
    const stepY = scaledHeight * 1;

    const imageCanvas = createCanvas(offsetWidth, offsetHeight);
    const imageContext = setupCanvasContext(imageCanvas);

    drawPattern(
      imageContext,
      (drawContext, x, y) =>
        drawContext.drawImage(
          imgElem,
          x - scaledWidth / 2,
          y - scaledHeight / 2,
          scaledWidth,
          scaledHeight,
        ),
      stepX,
      stepY,
      imageCanvas,
    );

    const dataUrl = imageCanvas.toDataURL('image/png');
    imageCache.set(cacheKey, dataUrl);
    return dataUrl;
  } catch (error) {
    // set the fallback image
    return getAsset('/waldo-logo-white-words.svg');
  }
};

const handleImageError = () => {
  // set the fallback image
  return getAsset('/waldo-logo-white-words.svg');
};

export const useWatermarkImage = ({
  text,
  fontSize,
  imageUrl,
  wallpaperMode,
  containerRef,
}) => {
  const [watermarkImage, setWatermarkImage] = useState(null);
  const previousProcessedImageRef = useRef(null);

  const getMemoizedCacheKey = useCallback(
    (url, width, height, mode) => getCacheKey(url, width, height, mode),
    [],
  );

  useEffect(() => {
    if (!containerRef.current) return;
    const { offsetWidth, offsetHeight } = containerRef.current;
    if (!offsetWidth || !offsetHeight) return;

    // Try to get from cache first for image mode
    if (wallpaperMode === WALLPAPER_WATERMARK_MODE.IMAGE && imageUrl) {
      const cacheKey = getMemoizedCacheKey(
        imageUrl,
        offsetWidth,
        offsetHeight,
        wallpaperMode,
      );
      if (imageCache.has(cacheKey)) {
        setWatermarkImage(imageCache.get(cacheKey));
        return;
      }
    }

    const canvas = createCanvas(offsetWidth, offsetHeight);
    const context = setupCanvasContext(canvas);

    if (wallpaperMode === WALLPAPER_WATERMARK_MODE.TEXT) {
      const cacheKey = getMemoizedCacheKey(
        text,
        offsetWidth,
        offsetHeight,
        wallpaperMode,
      );
      if (imageCache.has(cacheKey)) {
        setWatermarkImage(imageCache.get(cacheKey));
        return;
      }

      // Adjust the font size to appear correctly
      const fontScalingFactor = 0.4;
      const adjustedFontSize = fontSize * fontScalingFactor;

      context.font = `${adjustedFontSize}px Arial`;
      context.fillStyle = 'rgba(255, 255, 255, 0.9)';
      context.textAlign = 'center';
      context.textBaseline = 'middle';

      const textWidth = context.measureText(text).width;
      const stepX = textWidth + 15;
      const stepY = adjustedFontSize * 2.5;

      drawPattern(
        context,
        (drawContext, x, y) => drawContext.fillText(text, x, y),
        stepX,
        stepY,
        canvas,
      );

      const dataUrl = canvas.toDataURL('image/png');
      imageCache.set(cacheKey, dataUrl);
      setWatermarkImage(dataUrl);
    } else if (wallpaperMode === WALLPAPER_WATERMARK_MODE.IMAGE) {
      if (!imageUrl) {
        setWatermarkImage(getAsset('/waldo-logo-white-words.svg'));
        return;
      }

      setWatermarkImage(null);

      const cacheKey = getMemoizedCacheKey(
        imageUrl,
        offsetWidth,
        offsetHeight,
        wallpaperMode,
      );

      const img = new Image();
      img.crossOrigin = 'anonymous';

      if (imageUrl.startsWith('blob:') || imageUrl.startsWith('data:')) {
        img.src = imageUrl;
      } else {
        const cacheBuster = `${Date.now()}-${Math.random()
          .toString(36)
          .substring(2, 10)}`;
        const fullUrl = `${imageUrl}${
          imageUrl.includes('?') ? '&' : '?'
        }${cacheBuster}`;
        img.src = fullUrl;
      }

      img.addEventListener('load', async () => {
        const dataUrl = await processImage(
          img,
          offsetWidth,
          offsetHeight,
          wallpaperMode,
          cacheKey,
        );
        setWatermarkImage(dataUrl);
        previousProcessedImageRef.current = dataUrl;
      });

      img.addEventListener('error', () => {
        const fallbackImage = handleImageError(null, imageUrl);
        setWatermarkImage(fallbackImage);
      });
    } else {
      setWatermarkImage(null);
    }

    // Cleanup function
    // eslint-disable-next-line consistent-return
    return () => {
      // We don't need to do anything here since cache is preserved
    };
  }, [
    text,
    fontSize,
    imageUrl,
    wallpaperMode,
    containerRef,
    getMemoizedCacheKey,
  ]);

  return watermarkImage;
};
